import { useCallback } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import type { SwaggerResponse } from '@/services/config';

import type { ApiError } from './errors';
import type { MutationResult } from './types';

/** A mutation as the UI sees it: domain variables in, the mapped body out. */
export type DomainMutation<TVariables, TData> = MutationResult<TData, TVariables>;

/** What an action-style mutation hook returns beside its action: pending state and last error. */
export interface MutationState {
    /** A request is in flight: react-query's `isPending`. */
    isLoading: boolean;
    error: ApiError | null;
    reset: () => void;
}

/** Callbacks a domain mutation hook forwards to react-query. They see no API variables. */
export interface DomainMutationCallbacks<TData = unknown> {
    onSuccess?: (data: TData) => unknown;
    onError?: (error: ApiError) => unknown;
    onSettled?: () => unknown;
}

/**
 * Turns domain callbacks into react-query options for a generated mutation hook. `map` turns the
 * response body into what `onSuccess` sees.
 */
export function toMutationOptions<TBody, TData>(
    callbacks: DomainMutationCallbacks<TData> | undefined,
    map: (body: TBody) => TData
): {
    onSuccess?: (response: SwaggerResponse<TBody>) => unknown;
    onError?: (error: ApiError) => unknown;
    onSettled?: () => unknown;
} {
    if (!callbacks) return {};
    const { onSuccess, onError, onSettled } = callbacks;
    return {
        ...(onSuccess && { onSuccess: (response) => onSuccess(map(response.data)) }),
        ...(onError && { onError }),
        ...(onSettled && { onSettled: () => onSettled() }),
    };
}

/**
 * Wraps a generated mutation so the UI passes domain variables and gets the mapped body back.
 * `toApi` and `fromApi` must be stable (module-level functions).
 */
export function useMappedMutation<TVariables, TApiVariables, TBody, TData>(
    mutation: UseMutationResult<SwaggerResponse<TBody>, ApiError, TApiVariables>,
    toApi: (variables: TVariables) => TApiVariables,
    fromApi: (body: TBody) => TData
): DomainMutation<TVariables, TData> {
    const { mutate: mutateApi, mutateAsync: mutateApiAsync } = mutation;

    const mutate = useCallback(
        (variables: TVariables) => mutateApi(toApi(variables)),
        [mutateApi, toApi]
    );
    const mutateAsync = useCallback(
        async (variables: TVariables) => fromApi((await mutateApiAsync(toApi(variables))).data),
        [mutateApiAsync, toApi, fromApi]
    );

    return {
        mutate,
        mutateAsync,
        isLoading: mutation.isPending,
        isSuccess: mutation.isSuccess,
        isError: mutation.isError,
        error: mutation.error,
        reset: mutation.reset,
    };
}

/** For mutations whose response body the UI never reads. */
export function ignoreBody(): void {}
